import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { RxHamburgerMenu, RxCross2 } from 'react-icons/rx';
import { FiChevronDown } from 'react-icons/fi';
import './NavbarDemo.css';

// Menu data
const menuItems = [
  { name: 'Home', path: '/' },
  { name: 'Blogs', path: '/blogs' },
  {
    name: 'About Us',
    path: '#',
    submenu: [
      { name: 'Vision & Mission', path: '/vision-mission' },
      { name: 'About the Founder', path: '/about-founder' },
      { name: "Founder's Message", path: '/founder-message' },
      { name: 'Team', path: '/teams' },
    ],
  },
  {
    name: 'Portfolio',
    path: '#',
    submenu: [
      { name: 'Web Builder', path: '/web-builder' },
      { name: 'LARIS', path: '/laris' },
      { name: 'MAPS', path: '/maps' },
      { name: 'Quest', path: '/quest' },
    ],
  },
  { name: 'Contact Us', path: '/contact-us' },
];

// Reusable dropdown hook
const useDropdown = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const handleMouseEnter = (index) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setOpenDropdown(index);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  return { openDropdown, setOpenDropdown, handleMouseEnter, handleMouseLeave };
};

// Navbar Style 1: Standard Horizontal with Framer Motion
const NavbarStyle1 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const { openDropdown, setOpenDropdown, handleMouseEnter, handleMouseLeave } = useDropdown();

  return (
    <motion.nav
      className={`navbar-demo navbar-style-1 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>

        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, index) => (
                <div key={index} className="mobile-menu-item">
                  {item.submenu ? (
                    <div>
                      <button
                        className="mobile-menu-link"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        {item.name}
                        <FiChevronDown
                          style={{
                            transform:
                              openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </button>
                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mobile-submenu"
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link
                                key={subIndex}
                                to={sub.path}
                                className="mobile-submenu-link"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={item.path} className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/request-a-demo" className="mobile-cta-btn" onClick={() => setIsMenuOpen(false)}>
                Request a Demo
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="desktop-menu">
          <ul className="desktop-menu-list">
            {menuItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div
                    className="dropdown-wrapper"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                    >
                      {item.name}
                      <FiChevronDown
                        style={{
                          transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 2: Centered Logo
const NavbarStyle2 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const { openDropdown, setOpenDropdown, handleMouseEnter, handleMouseLeave } = useDropdown();

  return (
    <motion.nav
      className={`navbar-demo navbar-style-2 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>

        <div className="desktop-menu-left">
          <ul className="desktop-menu-list">
            {menuItems.slice(0, Math.ceil(menuItems.length / 2)).map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div
                    className="dropdown-wrapper"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                    >
                      {item.name}
                      <FiChevronDown
                        style={{
                          transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <motion.div
          className="navbar-logo-centered"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>

        <div className="desktop-menu-right">
          <ul className="desktop-menu-list">
            {menuItems.slice(Math.ceil(menuItems.length / 2)).map((item, index) => (
              <li key={index + Math.ceil(menuItems.length / 2)} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                      onHoverStart={() =>
                        setOpenDropdown(index + Math.ceil(menuItems.length / 2))
                      }
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index + Math.ceil(menuItems.length / 2) && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button">
              Request a Demo
            </Link>
          </motion.div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, index) => (
                <div key={index} className="mobile-menu-item">
                  {item.submenu ? (
                    <div>
                      <button
                        className="mobile-menu-link"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        {item.name}
                        <FiChevronDown
                          style={{
                            transform:
                              openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </button>
                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mobile-submenu"
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link
                                key={subIndex}
                                to={sub.path}
                                className="mobile-submenu-link"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={item.path} className="mobile-menu-link">
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/request-a-demo" className="mobile-cta-btn">
                Request a Demo
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Navbar Style 3: Transparent to Solid on Scroll
const NavbarStyle3 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const { scrollY } = useScroll();
  const backgroundColor = useTransform(
    scrollY,
    [0, 100],
    ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 1)']
  );
  const boxShadow = useTransform(
    scrollY,
    [0, 100],
    ['0px 0px 0px rgba(0,0,0,0)', '0px 2px 10px rgba(0,0,0,0.1)']
  );

  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className="navbar-demo navbar-style-3"
      style={{ backgroundColor, boxShadow, position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>

        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, index) => (
                <div key={index} className="mobile-menu-item">
                  {item.submenu ? (
                    <div>
                      <button
                        className="mobile-menu-link"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        {item.name}
                        <FiChevronDown
                          style={{
                            transform:
                              openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </button>
                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mobile-submenu"
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link
                                key={subIndex}
                                to={sub.path}
                                className="mobile-submenu-link"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={item.path} className="mobile-menu-link">
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/request-a-demo" className="mobile-cta-btn">
                Request a Demo
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="desktop-menu">
          <ul className="desktop-menu-list">
            {menuItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 4: Minimal Style
const NavbarStyle4 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const { openDropdown, setOpenDropdown, handleMouseEnter, handleMouseLeave } = useDropdown();

  return (
    <motion.nav
      className={`navbar-demo navbar-style-4 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>

        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, index) => (
                <div key={index} className="mobile-menu-item">
                  {item.submenu ? (
                    <div>
                      <button
                        className="mobile-menu-link"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        {item.name}
                        <FiChevronDown
                          style={{
                            transform:
                              openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </button>
                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mobile-submenu"
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link
                                key={subIndex}
                                to={sub.path}
                                className="mobile-submenu-link"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={item.path} className="mobile-menu-link">
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/request-a-demo" className="mobile-cta-btn">
                Request a Demo
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="desktop-menu">
          <ul className="desktop-menu-list minimal">
            {menuItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle minimal-link"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link minimal-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button minimal-cta">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 5: Sticky with Shrink Effect
const NavbarStyle5 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const { openDropdown, setOpenDropdown, handleMouseEnter, handleMouseLeave } = useDropdown();

  return (
    <motion.nav
      className={`navbar-demo navbar-style-5 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-logo"
          animate={{
            scale: isScrolled ? 0.8 : 1,
            transition: { duration: 0.3 },
          }}
          whileHover={{ scale: isScrolled ? 0.85 : 1.05 }}
          whileTap={{ scale: isScrolled ? 0.75 : 0.95 }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>

        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
            >
              {menuItems.map((item, index) => (
                <div key={index} className="mobile-menu-item">
                  {item.submenu ? (
                    <div>
                      <button
                        className="mobile-menu-link"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        {item.name}
                        <FiChevronDown
                          style={{
                            transform:
                              openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </button>
                      <AnimatePresence>
                        {openDropdown === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mobile-submenu"
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link
                                key={subIndex}
                                to={sub.path}
                                className="mobile-submenu-link"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={item.path} className="mobile-menu-link">
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/request-a-demo" className="mobile-cta-btn">
                Request a Demo
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="desktop-menu">
          <ul className="desktop-menu-list">
            {menuItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link
                              key={subIndex}
                              to={sub.path}
                              className="dropdown-item"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Reusable Menu Components
const MobileMenu = ({ menuItems, isMenuOpen, openDropdown, setOpenDropdown, setIsMenuOpen, ctaClass = '' }) => (
  <AnimatePresence>
    {isMenuOpen && (
      <motion.div
        className="mobile-menu"
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -100 }}
        transition={{ duration: 0.3 }}
      >
        {menuItems.map((item, index) => (
          <div key={index} className="mobile-menu-item">
            {item.submenu ? (
              <div>
                <button
                  className="mobile-menu-link"
                  onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                >
                  {item.name}
                  <FiChevronDown
                    style={{
                      transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  />
                </button>
                <AnimatePresence>
                  {openDropdown === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mobile-submenu"
                    >
                      {item.submenu.map((sub, subIndex) => (
                        <Link key={subIndex} to={sub.path} className="mobile-submenu-link">
                          {sub.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to={item.path} className="mobile-menu-link" onClick={() => setIsMenuOpen(false)}>
                {item.name}
              </Link>
            )}
          </div>
        ))}
        <Link to="/request-a-demo" className={`mobile-cta-btn ${ctaClass}`} onClick={() => setIsMenuOpen(false)}>
          Request a Demo
        </Link>
      </motion.div>
    )}
  </AnimatePresence>
);

const DesktopMenu = ({ menuItems, openDropdown, setOpenDropdown, linkClass = '', ctaClass = '' }) => {
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const handleMouseEnter = (index) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setOpenDropdown(index);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  return (
    <div className="desktop-menu">
      <ul className={`desktop-menu-list ${linkClass}`}>
        {menuItems.map((item, index) => (
          <li key={index} className="desktop-menu-item">
            {item.submenu ? (
              <div
                className="dropdown-wrapper"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                <motion.button
                  className={`desktop-menu-link dropdown-toggle ${linkClass}`}
                  whileHover={{ y: -2 }}
                >
                  {item.name}
                  <FiChevronDown
                    style={{
                      transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </motion.button>
                <AnimatePresence>
                  {openDropdown === index && (
                    <motion.div
                      className="dropdown-menu"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.submenu.map((sub, subIndex) => (
                        <Link
                          key={subIndex}
                          to={sub.path}
                          className="dropdown-item"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <motion.div whileHover={{ y: -2 }}>
                <Link to={item.path} className={`desktop-menu-link ${linkClass}`}>
                  {item.name}
                </Link>
              </motion.div>
            )}
          </li>
        ))}
      </ul>
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Link to="/request-a-demo" className={`cta-button ${ctaClass}`}>
          Request a Demo
        </Link>
      </motion.div>
    </div>
  );
};

// Navbar Style 6: Glass Morphism
const NavbarStyle6 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-6 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu menuItems={menuItems} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
      </div>
    </motion.nav>
  );
};

// Navbar Style 7: Dark Theme
const NavbarStyle7 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-7 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn dark"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
          ctaClass="dark-cta"
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="dark-link"
          ctaClass="dark-cta"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 8: Gradient Background
const NavbarStyle8 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-8 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
          ctaClass="gradient-cta"
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="gradient-link"
          ctaClass="gradient-cta"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 9: Floating Navbar
const NavbarStyle9 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-9 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: isScrolled ? 20 : 40 }}
      transition={{ duration: 0.3 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu menuItems={menuItems} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
      </div>
    </motion.nav>
  );
};

// Navbar Style 10: Split Color
const NavbarStyle10 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const leftItems = menuItems.slice(0, Math.ceil(menuItems.length / 2));
  const rightItems = menuItems.slice(Math.ceil(menuItems.length / 2));

  return (
    <motion.nav
      className={`navbar-demo navbar-style-10 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <div className="desktop-menu-left">
          <ul className="desktop-menu-list">
            {leftItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link key={subIndex} to={sub.path} className="dropdown-item">
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link">
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="desktop-menu-right">
          <ul className="desktop-menu-list">
            {rightItems.map((item, index) => {
              const actualIndex = index + Math.ceil(menuItems.length / 2);
              return (
                <li key={actualIndex} className="desktop-menu-item">
                  {item.submenu ? (
                    <div className="dropdown-wrapper">
                      <motion.button
                        className="desktop-menu-link dropdown-toggle"
                        whileHover={{ y: -2 }}
                        onHoverStart={() => setOpenDropdown(actualIndex)}
                        onHoverEnd={() => setOpenDropdown(null)}
                      >
                        {item.name}
                        <FiChevronDown />
                      </motion.button>
                      <AnimatePresence>
                        {openDropdown === actualIndex && (
                          <motion.div
                            className="dropdown-menu"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.submenu.map((sub, subIndex) => (
                              <Link key={subIndex} to={sub.path} className="dropdown-item">
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <motion.div whileHover={{ y: -2 }}>
                      <Link to={item.path} className="desktop-menu-link">
                        {item.name}
                      </Link>
                    </motion.div>
                  )}
                </li>
              );
            })}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 11: Mega Menu Style
const NavbarStyle11 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-11 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="mega-link"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 12: Icon-Based Navigation
const NavbarStyle12 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-12 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="icon-link"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 13: Sidebar Style (Desktop shows sidebar, mobile shows normal)
const NavbarStyle13 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <>
      <motion.nav
        className={`navbar-demo navbar-style-13 ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: 0 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
      >
        <div className="navbar-container">
          <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <img src="/assets/logo.svg" alt="logo" />
          </motion.div>
          <motion.button
            className="hamburger-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
          </motion.button>
          <MobileMenu
            menuItems={menuItems}
            isMenuOpen={isMenuOpen}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            setIsMenuOpen={setIsMenuOpen}
            ctaClass="sidebar-cta"
          />
          <DesktopMenu
            menuItems={menuItems}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            linkClass="sidebar-link"
            ctaClass="sidebar-cta"
          />
        </div>
      </motion.nav>
      {/* Sidebar for desktop */}
      <motion.aside
        className="desktop-sidebar"
        initial={{ x: -300 }}
        animate={{ x: isMenuOpen ? 0 : -300 }}
        transition={{ duration: 0.3 }}
      >
        <div className="sidebar-content">
          <div className="sidebar-header">
            <img src="/assets/logo.svg" alt="logo" />
            <button onClick={() => setIsMenuOpen(false)} className="sidebar-close">
              <RxCross2 />
            </button>
          </div>
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => (
              <li key={index}>
                {item.submenu ? (
                  <div>
                    <button
                      className="sidebar-link"
                      onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                    >
                      {item.name}
                      <FiChevronDown
                        style={{
                          transform: openDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      />
                    </button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.ul
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="sidebar-submenu"
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <li key={subIndex}>
                              <Link to={sub.path}>{sub.name}</Link>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link to={item.path} className="sidebar-link" onClick={() => setIsMenuOpen(false)}>
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          <Link to="/request-a-demo" className="sidebar-cta-btn" onClick={() => setIsMenuOpen(false)}>
            Request a Demo
          </Link>
        </div>
      </motion.aside>
      {isMenuOpen && (
        <motion.div
          className="sidebar-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

// Navbar Style 14: Animated Underline
const NavbarStyle14 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-14 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <div className="desktop-menu">
          <ul className="desktop-menu-list underline-nav">
            {menuItems.map((item, index) => (
              <li
                key={index}
                className="desktop-menu-item"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle underline-link"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      {item.name}
                      <FiChevronDown />
                      {hoveredIndex === index && (
                        <motion.span
                          className="underline-animation"
                          layoutId="underline"
                          initial={false}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      )}
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link key={subIndex} to={sub.path} className="dropdown-item">
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link underline-link">
                      {item.name}
                      {hoveredIndex === index && (
                        <motion.span
                          className="underline-animation"
                          layoutId="underline"
                          initial={false}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button underline-cta">
              Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 15: Neumorphism
const NavbarStyle15 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-15 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
          ctaClass="neumorphism-cta"
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="neumorphism-link"
          ctaClass="neumorphism-cta"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 16: 3D Effect
const NavbarStyle16 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-16 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div
          className="navbar-logo"
          whileHover={{ scale: 1.05, rotateY: 5 }}
          whileTap={{ scale: 0.95 }}
          style={{ perspective: '1000px' }}
        >
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1, rotateZ: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="3d-link"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 17: Animated Gradient Text
const NavbarStyle17 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-17 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="gradient-text-link"
          ctaClass="gradient-text-cta"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 18: Card Style
const NavbarStyle18 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-18 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="card-link"
          ctaClass="card-cta"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 19: Modern Glass with Border
const NavbarStyle19 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-19 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="glass-border-link"
        />
      </div>
    </motion.nav>
  );
};

// Navbar Style 20: Progress Bar Navbar
const NavbarStyle20 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const { scrollYProgress } = useScroll();

  return (
    <motion.nav
      className={`navbar-demo navbar-style-20 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <motion.div
        className="scroll-progress-bar"
        style={{
          scaleX: scrollYProgress,
          transformOrigin: 'left',
        }}
      />
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu menuItems={menuItems} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
      </div>
    </motion.nav>
  );
};

// Navbar Style 21: Modern Minimal with Icons
const NavbarStyle21 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  const menuIcons = {
    'Home': '🏠',
    'Blogs': '📝',
    'About Us': '👥',
    'Portfolio': '💼',
    'Contact Us': '📧',
  };

  return (
    <motion.nav
      className={`navbar-demo navbar-style-21 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <div className="desktop-menu">
          <ul className="desktop-menu-list icon-minimal-nav">
            {menuItems.map((item, index) => (
              <li key={index} className="desktop-menu-item">
                {item.submenu ? (
                  <div className="dropdown-wrapper">
                    <motion.button
                      className="desktop-menu-link dropdown-toggle icon-minimal-link"
                      whileHover={{ y: -2 }}
                      onHoverStart={() => setOpenDropdown(index)}
                      onHoverEnd={() => setOpenDropdown(null)}
                    >
                      <span className="menu-icon">{menuIcons[item.name] || '📄'}</span>
                      {item.name}
                      <FiChevronDown />
                    </motion.button>
                    <AnimatePresence>
                      {openDropdown === index && (
                        <motion.div
                          className="dropdown-menu"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.submenu.map((sub, subIndex) => (
                            <Link key={subIndex} to={sub.path} className="dropdown-item">
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link to={item.path} className="desktop-menu-link icon-minimal-link">
                      <span className="menu-icon">{menuIcons[item.name] || '📄'}</span>
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/request-a-demo" className="cta-button icon-minimal-cta">
              ✨ Request a Demo
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.nav>
  );
};

// Navbar Style 22: Animated Background Pattern
const NavbarStyle22 = ({ isScrolled, isMenuOpen, setIsMenuOpen }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <motion.nav
      className={`navbar-demo navbar-style-22 ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="animated-bg-pattern" />
      <div className="navbar-container">
        <motion.div className="navbar-logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <img src="/assets/logo.svg" alt="logo" />
        </motion.div>
        <motion.button
          className="hamburger-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isMenuOpen ? <RxCross2 /> : <RxHamburgerMenu />}
        </motion.button>
        <MobileMenu
          menuItems={menuItems}
          isMenuOpen={isMenuOpen}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          setIsMenuOpen={setIsMenuOpen}
        />
        <DesktopMenu
          menuItems={menuItems}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          linkClass="pattern-link"
        />
      </div>
    </motion.nav>
  );
};

// Main Demo Component
function NavbarDemo() {
  const [selectedStyle, setSelectedStyle] = useState('style1');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      if (scrollTop > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navbarStyles = {
    style1: {
      name: 'Standard Horizontal',
      component: NavbarStyle1,
      description: 'Classic horizontal navbar with logo on left, menu on right',
    },
    style2: {
      name: 'Centered Logo',
      component: NavbarStyle2,
      description: 'Logo centered with menu items on both sides',
    },
    style3: {
      name: 'Transparent to Solid',
      component: NavbarStyle3,
      description: 'Transparent navbar that becomes solid on scroll',
    },
    style4: {
      name: 'Minimal Style',
      component: NavbarStyle4,
      description: 'Clean, minimal design with subtle styling',
    },
    style5: {
      name: 'Sticky Shrink',
      component: NavbarStyle5,
      description: 'Navbar shrinks in height when scrolling',
    },
    style6: {
      name: 'Glass Morphism',
      component: NavbarStyle6,
      description: 'Frosted glass effect with backdrop blur',
    },
    style7: {
      name: 'Dark Theme',
      component: NavbarStyle7,
      description: 'Dark background with light text',
    },
    style8: {
      name: 'Gradient Background',
      component: NavbarStyle8,
      description: 'Beautiful gradient background with smooth transitions',
    },
    style9: {
      name: 'Floating Navbar',
      component: NavbarStyle9,
      description: 'Floating navbar with margin from top',
    },
    style10: {
      name: 'Split Color',
      component: NavbarStyle10,
      description: 'Split color design with left and right sections',
    },
    style11: {
      name: 'Mega Menu',
      component: NavbarStyle11,
      description: 'Wide dropdown menus for complex navigation',
    },
    style12: {
      name: 'Icon-Based',
      component: NavbarStyle12,
      description: 'Modern icon-based navigation style',
    },
    style13: {
      name: 'Sidebar Style',
      component: NavbarStyle13,
      description: 'Sidebar navigation for desktop, mobile menu for mobile',
    },
    style14: {
      name: 'Animated Underline',
      component: NavbarStyle14,
      description: 'Smooth animated underline on hover',
    },
    style15: {
      name: 'Neumorphism',
      component: NavbarStyle15,
      description: 'Soft shadow neumorphic design',
    },
    style16: {
      name: '3D Effect',
      component: NavbarStyle16,
      description: '3D transform effects on hover',
    },
    style17: {
      name: 'Gradient Text',
      component: NavbarStyle17,
      description: 'Animated gradient text effects',
    },
    style18: {
      name: 'Card Style',
      component: NavbarStyle18,
      description: 'Card-based modern design',
    },
    style19: {
      name: 'Glass Border',
      component: NavbarStyle19,
      description: 'Glass morphism with animated border',
    },
    style20: {
      name: 'Progress Bar',
      component: NavbarStyle20,
      description: 'Scroll progress indicator at top',
    },
    style21: {
      name: 'Icon Minimal',
      component: NavbarStyle21,
      description: 'Modern minimal with emoji icons',
    },
    style22: {
      name: 'Animated Pattern',
      component: NavbarStyle22,
      description: 'Animated background pattern',
    },
  };

  const CurrentNavbar = navbarStyles[selectedStyle].component;

  useEffect(() => {
    // Set body background when component mounts
    document.body.style.backgroundColor = '#f5f5f5';
    document.documentElement.style.backgroundColor = '#f5f5f5';
    
    return () => {
      // Reset on unmount
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  return (
    <div className="navbar-demo-wrapper">
      {/* Style Selector */}
      <div className="navbar-style-selector">
        <h3 className="selector-title">Navbar Style Options</h3>
        <div className="style-buttons">
          {Object.entries(navbarStyles).map(([key, style]) => (
            <button
              key={key}
              className={`style-btn ${selectedStyle === key ? 'active' : ''}`}
              onClick={() => {
                setSelectedStyle(key);
                setIsMenuOpen(false);
              }}
            >
              {style.name}
            </button>
          ))}
        </div>
        <p className="style-description">
          {navbarStyles[selectedStyle].description}
        </p>
      </div>

      {/* Current Navbar */}
      <CurrentNavbar
        isScrolled={isScrolled}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      {/* Demo Content */}
      <div className="demo-content">
        <div className="demo-section">
          <h2>Scroll down to see navbar effects</h2>
          <p>
            Try scrolling to see how each navbar style responds. Some styles
            change background color, shrink, or become solid on scroll.
          </p>
        </div>

        <div className="demo-section">
          <h3>22 Beautiful Navbar Styles Available</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            <div>
              <h4>Classic Styles</h4>
              <ul>
                <li>1. Standard Horizontal</li>
                <li>2. Centered Logo</li>
                <li>3. Transparent to Solid</li>
                <li>4. Minimal Style</li>
                <li>5. Sticky Shrink</li>
              </ul>
            </div>
            <div>
              <h4>Modern Effects</h4>
              <ul>
                <li>6. Glass Morphism</li>
                <li>7. Dark Theme</li>
                <li>8. Gradient Background</li>
                <li>9. Floating Navbar</li>
                <li>10. Split Color</li>
              </ul>
            </div>
            <div>
              <h4>Advanced Features</h4>
              <ul>
                <li>11. Mega Menu</li>
                <li>12. Icon-Based</li>
                <li>13. Sidebar Style</li>
                <li>14. Animated Underline</li>
                <li>15. Neumorphism</li>
              </ul>
            </div>
            <div>
              <h4>Premium Designs</h4>
              <ul>
                <li>16. 3D Effect</li>
                <li>17. Gradient Text</li>
                <li>18. Card Style</li>
                <li>19. Glass Border</li>
                <li>20. Progress Bar</li>
                <li>21. Icon Minimal</li>
                <li>22. Animated Pattern</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="demo-section">
          <h3>Features Demonstrated</h3>
          <ul>
            <li>✅ Smooth animations with Framer Motion</li>
            <li>✅ Responsive mobile menu</li>
            <li>✅ Dropdown menus with hover effects</li>
            <li>✅ Scroll-triggered effects</li>
            <li>✅ Logo animations</li>
            <li>✅ CTA button styling</li>
            <li>✅ Glass morphism effects</li>
            <li>✅ Dark/light themes</li>
            <li>✅ Gradient backgrounds</li>
            <li>✅ Sidebar navigation</li>
          </ul>
        </div>

        <div className="demo-section">
          <h3>Try These Interactions</h3>
          <ul>
            <li>🖱️ Hover over menu items</li>
            <li>📱 Click hamburger menu on mobile</li>
            <li>📜 Scroll to see scroll effects</li>
            <li>🎨 Switch between 13 different styles</li>
            <li>🔽 Hover over dropdown menus</li>
            <li>💫 Try the floating navbar (Style 9)</li>
            <li>🌙 Check out the dark theme (Style 7)</li>
            <li>🌈 See the gradient effects (Style 8)</li>
            <li>📂 Open sidebar on desktop (Style 13)</li>
          </ul>
        </div>

        {/* Spacer for scrolling */}
        <div style={{ height: '200vh', padding: '20px' }}>
          <h3>Keep scrolling to see navbar behavior...</h3>
          <p>This extra content helps demonstrate scroll effects.</p>
        </div>
      </div>
    </div>
  );
}

export default NavbarDemo;
